import { useState, useRef, useCallback } from 'react'

export type VoiceState = 'idle' | 'recording' | 'processing'

export function useVoice() {
  const [state, setState] = useState<VoiceState>('idle')
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  // Pre-acquired stream so first recording doesn't hit permission delay
  const warmStreamRef = useRef<MediaStream | null>(null)

  /** Pre-acquire mic permission + warm up the audio pipeline.
   *  Call this from a user gesture (e.g. first tap) before actual recording. */
  const warmUp = useCallback(async () => {
    if (warmStreamRef.current) return
    try {
      warmStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 48000 },
      })
    } catch {
      // Ignore — startRecording will retry
    }
  }, [])

  const startRecording = useCallback(async () => {
    try {
      // Reuse warm stream if available, otherwise request fresh
      const stream =
        warmStreamRef.current ??
        (await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000,
          },
        }))
      warmStreamRef.current = null // consumed
      streamRef.current = stream
      chunksRef.current = []

      // Prefer opus in webm, fall back to whatever is available
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4'

      const recorder = new MediaRecorder(stream, { mimeType })

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorderRef.current = recorder
      recorder.start(100) // Collect in 100ms chunks
      setState('recording')
    } catch (err) {
      console.error('Microphone access denied:', err)
      setState('idle')
      throw err
    }
  }, [])

  const stopRecording = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current
      if (!recorder || recorder.state === 'inactive') {
        reject(new Error('No active recording'))
        return
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType,
        })
        chunksRef.current = []

        // Stop all tracks to release microphone
        streamRef.current?.getTracks().forEach((t) => t.stop())
        streamRef.current = null
        mediaRecorderRef.current = null

        setState('processing')
        resolve(blob)
      }

      recorder.stop()
    })
  }, [])

  const cancel = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    mediaRecorderRef.current = null
    chunksRef.current = []
    setState('idle')
  }, [])

  const setIdle = useCallback(() => setState('idle'), [])
  const setProcessing = useCallback(() => setState('processing'), [])

  return {
    state,
    startRecording,
    stopRecording,
    cancel,
    setIdle,
    setProcessing,
    warmUp,
  }
}
